/**
 * 导入 Excel 单据：读文件 → 给出导入结果（多个工作表让用户挑）→ 点「打开画布」才建模板。
 *
 * 为什么不读完就直接开画布：读文件是异步的，等它读完，浏览器已经不认为这是
 * 用户点击触发的了，新标签页会被拦截。所以停在这一步，让用户再点一下 ——
 * 顺便把「绑了几个字段、哪些没导进来」说清楚。
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Radio, Space, Spin, Typography } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import type { FieldMeta } from '../api/types';
import type { ContractTemplate } from '../contract/types';
import { sheetToSections, type XSheet } from '../contract/importExcel';

interface Props {
  file: File | null;
  fieldMetas: FieldMeta[];
  onClose: () => void;
  /** 用户确认后交出模板主体，由调用方落库并打开画布（必须在这次点击里同步调用）。 */
  onApply: (tpl: Pick<ContractTemplate, 'name' | 'sections' | 'settings'>) => void;
}

export function ExcelImportModal({ file, fieldMetas, onClose, onApply }: Props) {
  const [sheets, setSheets] = useState<XSheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState(0);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setSheets(null);
    setError(null);
    setPick(0);
    void import('../utils/readXlsx')
      .then(({ readXlsx }) => readXlsx(file))
      .then((s) => !cancelled && setSheets(s))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [file]);

  const sheet = sheets?.[pick] ?? null;
  const result = useMemo(() => (sheet ? sheetToSections(sheet, fieldMetas) : null), [sheet, fieldMetas]);
  const baseName = (file?.name ?? 'Excel').replace(/\.xlsx?$/i, '');

  const apply = () => {
    if (!result || !sheet) return;
    onApply({
      name: sheets && sheets.length > 1 ? `${baseName} · ${sheet.name}` : baseName,
      sections: result.sections,
      settings: {
        paperSize: 'A4',
        orientation: result.landscape ? 'landscape' : 'portrait',
        marginMm: 15,
        baseFontSizePx: result.baseFontSizePx,
      },
    });
  };

  const tables = result?.sections.filter((s) => s.type === 'free_table').length ?? 0;

  return (
    <Modal
      open={!!file}
      onCancel={onClose}
      title={
        <span>
          <FileExcelOutlined style={{ color: '#1d6f42', marginRight: 6 }} />
          导入 Excel 单据
        </span>
      }
      width={420}
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" disabled={!result || result.sections.length === 0} onClick={apply}>
            打开画布继续编辑
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {error ? (
        <Alert type="error" showIcon message="导入失败" description={error} />
      ) : !sheets ? (
        <div style={{ padding: '28px 0', textAlign: 'center' }}>
          <Spin tip="正在读取表格…">
            <div style={{ height: 40 }} />
          </Spin>
        </div>
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {sheets.length > 1 && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                这个文件有 {sheets.length} 个工作表，选一个导入
              </Typography.Text>
              <Radio.Group
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}
                optionType="button"
                size="small"
                options={sheets.map((s, i) => ({ label: s.name, value: i }))}
              />
            </div>
          )}
          {result && result.sections.length === 0 ? (
            <Alert type="warning" showIcon message="这个工作表里没有识别出可用的内容" />
          ) : (
            result && (
              <div style={{ fontSize: 12.5, lineHeight: 1.9, color: 'var(--ink-2)' }}>
                <div>
                  识别出 <b>{result.sections.length}</b> 个区块，其中表格 <b>{tables}</b> 张；
                  合并单元格、列宽、加粗、底色都按原表还原。
                </div>
                <div>
                  {result.bound > 0 ? (
                    <>
                      按字段名自动绑定了 <b>{result.bound}</b> 处（如「客户名称：」右边的空格）。
                    </>
                  ) : (
                    <>没有自动绑上字段：进画布后点左侧字段，或在格子里写 {'{{字段名}}'}。</>
                  )}
                </div>
                {result.landscape && <div>Excel 里设的是横向打印，模板也设成了横向。</div>}
              </div>
            )
          )}
          {result && (result.images > 0 || result.truncatedRows > 0) && (
            <Alert
              type="info"
              showIcon
              style={{ fontSize: 12 }}
              message={
                <>
                  {result.images > 0 && <div>表里的 {result.images} 张图片（logo、印章）没有导入，进画布后在「印章 · 签名区域」里单独加。</div>}
                  {result.truncatedRows > 0 && <div>表格太长，只导入了前 300 行（截掉 {result.truncatedRows} 行）。</div>}
                </>
              }
            />
          )}
          <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
            明细要随记录自动加行：进画布选中那张表，右侧「数据来源」选「动态明细 · 关联子表」。
          </Typography.Text>
        </Space>
      )}
    </Modal>
  );
}
